import React from 'react';
import { Box, Typography, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  dialContainer: {
    position: 'relative',
    width: 200,
    height: 200,
    margin: '0 auto',
  },
  dialBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  dialProgress: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transform: 'rotate(-90deg)',
  },
  dialText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    zIndex: 2,
  },
  dialValue: {
    fontWeight: 700,
    fontSize: '2rem',
    lineHeight: 1,
  },
  dialLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  dialStats: {
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    fontSize: '0.8rem',
  },
}));

const DialMetric = ({
  activeUsers = 0,
  userCount = 0,
  title = "User Activity",
  color = "#1976d2",
  backgroundColor = "#e3f2fd",
  size = 200,
  strokeWidth = 12,
  getTranslation = (text) => text,
  preferredLanguage = 'EN',
}) => {
  const classes = useStyles();
  
  // Calculate percentage
  const percentage = userCount > 0 ? (activeUsers / userCount) * 100 : 0;
  const clampedPercentage = Math.min(Math.max(percentage, 0), 100);
  
  // Calculate SVG properties
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clampedPercentage / 100) * circumference;
  
  // Determine color based on percentage
  const getStatusColor = (pct) => {
    if (pct >= 80) return '#4caf50'; // Green
    if (pct >= 60) return '#ff9800'; // Orange
    if (pct >= 40) return '#f57c00'; // Dark Orange
    return '#f44336'; // Red
  };
  
  const statusColor = getStatusColor(clampedPercentage);
  
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <Paper elevation={3} style={{ padding: 16, borderRadius: 12, height: '100%' }}>
      <Typography variant="h6" style={{ textAlign: 'center', marginBottom: 16, fontWeight: 600 }}>
        {title}
      </Typography>
      
      <Box className={classes.dialContainer} style={{ width: size, height: size }}>
        {/* Background Circle */}
        <svg 
          className={classes.dialBackground}
          width={size} 
          height={size}
          viewBox={`0 0 ${size} ${size}`}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={backgroundColor}
            strokeWidth={strokeWidth}
            fill="none"
          />
        </svg>
        
        {/* Progress Circle */}
        <svg 
          className={classes.dialProgress}
          width={size} 
          height={size}
          viewBox={`0 0 ${size} ${size}`}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={statusColor}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 1s ease-in-out, stroke 0.3s ease',
            }}
          />
        </svg>
        
        {/* Center Text */}
        <Box className={classes.dialText}>
          <Typography className={classes.dialValue} style={{ color: statusColor }}>
            {Math.round(clampedPercentage)}%
          </Typography>
          <Typography className={classes.dialLabel}>
            {getTranslation('Active', preferredLanguage)}
          </Typography>
        </Box>
      </Box>
      
      {/* REMOVED: Redundant statistics - info is already shown in the pie chart */}
    </Paper>
  );
};

export default DialMetric;